import { createAction, props } from '@ngrx/store';
import { createAsyncAction } from '../../../core/async-state/utils/async-state-action.utils';
import { SYMPTOMS_QUESTION_GROUPS } from '../../../shared/components/charts/symptons-yes-no-bar-chart/definitions/symptoms-question-groups';
import {
  GeneralHealthHistoryData,
  SymptomHistoryData,
  WeightHistoryData,
} from '../../../shared/models';
import { BasicWebkData } from 'ngx-esprio-shared';
import { UserScoreResponse } from 'ngx-esprio-shared/lib/shared/models/account/responses/user-score-response.model';

export namespace UserDataApiActions {
  enum UserDataApiActionTypes {
    GetUserWeightHistoryData = '[User Data API] Get user weight history data',
    GetUserWeightHistoryDataSuccess = '[User Data API] Get user weight history data success',
    GetUserWeightHistoryDataFail = '[User Data API] Get user weight history data fail',

    GetUserSymptomsHistoryData = '[User Data API] Get user symptoms history data',
    GetUserSymptomsHistoryDataSuccess = '[User Data API] Get user symptoms history data success',
    GetUserSymptomsHistoryDataFail = '[User Data API] Get user symptoms history data fail',

    GetUserGeneralHealthHistoryData = '[User Data API] Get user general health history data',
    GetUserGeneralHealthHistoryDataSuccess = '[User Data API] Get user general health history data success',
    GetUserGeneralHealthHistoryDataFail = '[User Data API] Get user general health history data fail',

    GetUserBasicWebkData = '[User Data API] Get user basic webk data',
    GetUserBasicWebkDataSuccess = '[User Data API] Get user basic webk data success',
    GetUserBasicWebkDataFail = '[User Data API] Get user basic weebk data fail',

    GetUserWeightChangePercentage = '[User Data API] Get user weight change percentage',
    GetUserWeightChangePercentageSuccess = '[User Data API] Get user weight change percentage success',
    GetUserWeightChangePercentageFail = '[User Data API] Get user weight change percentage fail',

    GetUserTimeSnap = '[User Data API] Get user time snap',
    GetUserTimeSnapSuccess = '[User Data API] Get user time snap success',
    GetUserTimeSnapFail = '[User Data API] Get user time snap fail',

    GetUserScoreDetails = '[Survey API] Get user score details',
    GetUserScoreDetailsSuccess = '[Survey API] Get user score details success',
    GetUserScoreDetailsFail = '[Survey API] Get user score details fail',

    CheckDataAvailability = '[Survey API] Check Data Availability',
    CheckDataAvailabilitySuccess = '[Survey API] Check Data Availability Success',
    CheckDataAvailabilityFail = '[Survey API] Check Data Availability Fail',

    PurgeUserData = '[User Data] Purge user data',
  }

  export const getUserWeightHistoryData = createAsyncAction(
    UserDataApiActionTypes.GetUserWeightHistoryData,
    {
      asyncKey: UserDataApiActionTypes.GetUserWeightHistoryData,
      asyncState: 'start',
    },
    props<{ userId: string; monitoredUserId: string; startDate?: Date; endDate?: Date }>()
  );

  export const getUserWeightHistoryDataSuccess = createAsyncAction(
    UserDataApiActionTypes.GetUserWeightHistoryDataSuccess,
    {
      asyncKey: UserDataApiActionTypes.GetUserWeightHistoryData,
      asyncState: 'success',
    },
    props<{ payload: WeightHistoryData[] }>()
  );

  export const getUserWeightHistoryDataFail = createAsyncAction(
    UserDataApiActionTypes.GetUserWeightHistoryDataFail,
    {
      asyncKey: UserDataApiActionTypes.GetUserWeightHistoryData,
      asyncState: 'fail',
    }
  );

  export const getUserBasicWebkData = createAsyncAction(
    UserDataApiActionTypes.GetUserBasicWebkData,
    {
      asyncKey: UserDataApiActionTypes.GetUserBasicWebkData,
      asyncState: 'start',
    },
    props<{ userId: string; monitoredUserId: string; startDate?: Date; endDate?: Date }>()
  );

  export const getUserBasicWebkDataSuccess = createAsyncAction(
    UserDataApiActionTypes.GetUserBasicWebkDataSuccess,
    {
      asyncKey: UserDataApiActionTypes.GetUserBasicWebkData,
      asyncState: 'success',
    },
    props<{ payload: BasicWebkData[] }>()
  );

  export const getUserBasicWebkDataFail = createAsyncAction(
    UserDataApiActionTypes.GetUserBasicWebkDataFail,
    {
      asyncKey: UserDataApiActionTypes.GetUserBasicWebkData,
      asyncState: 'fail',
    }
  );

  export const getUserSymptomsHistoryData = createAsyncAction(
    UserDataApiActionTypes.GetUserSymptomsHistoryData,
    {
      asyncKey: UserDataApiActionTypes.GetUserSymptomsHistoryData,
      asyncState: 'start',
    },
    props<{
      userId: string;
      monitoredUserId: string;
      startDate?: Date;
      endDate?: Date;
      submissionId?: string;
      questionGroup?: SYMPTOMS_QUESTION_GROUPS;
    }>()
  );

  export const getUserSymptomsHistoryDataSuccess = createAsyncAction(
    UserDataApiActionTypes.GetUserSymptomsHistoryDataSuccess,
    {
      asyncKey: UserDataApiActionTypes.GetUserSymptomsHistoryData,
      asyncState: 'success',
    },
    props<{ payload: SymptomHistoryData[]; questionGroup?: SYMPTOMS_QUESTION_GROUPS }>()
  );

  export const getUserSymptomsHistoryDataFail = createAsyncAction(
    UserDataApiActionTypes.GetUserSymptomsHistoryDataFail,
    {
      asyncKey: UserDataApiActionTypes.GetUserSymptomsHistoryData,
      asyncState: 'fail',
    }
  );

  export const getUserGeneralHealthHistoryData = createAsyncAction(
    UserDataApiActionTypes.GetUserGeneralHealthHistoryData,
    {
      asyncKey: UserDataApiActionTypes.GetUserGeneralHealthHistoryData,
      asyncState: 'start',
    },
    props<{ userId: string; monitoredUserId: string; startDate?: Date; endDate?: Date }>()
  );

  export const getUserGeneralHealthHistoryDataSuccess = createAsyncAction(
    UserDataApiActionTypes.GetUserGeneralHealthHistoryDataSuccess,
    {
      asyncKey: UserDataApiActionTypes.GetUserGeneralHealthHistoryData,
      asyncState: 'success',
    },
    props<{ payload: GeneralHealthHistoryData[] }>()
  );

  export const getUserGeneralHealthHistoryDataFail = createAsyncAction(
    UserDataApiActionTypes.GetUserGeneralHealthHistoryDataFail,
    {
      asyncKey: UserDataApiActionTypes.GetUserGeneralHealthHistoryData,
      asyncState: 'fail',
    }
  );

  export const getUserWeightChangePercentage = createAsyncAction(
    UserDataApiActionTypes.GetUserWeightChangePercentage,
    {
      asyncKey: UserDataApiActionTypes.GetUserWeightChangePercentage,
      asyncState: 'start',
    },
    props<{ userId: string; monitoredUserId: string; startDate: Date; endDate: Date }>()
  );

  export const getUserWeightChangePercentageSuccess = createAsyncAction(
    UserDataApiActionTypes.GetUserWeightChangePercentageSuccess,
    {
      asyncKey: UserDataApiActionTypes.GetUserWeightChangePercentage,
      asyncState: 'success',
    },
    props<{ payload: { weightChangePercentage: number; startDate: Date; endDate: Date } }>()
  );

  export const getUserWeightChangePercentageFail = createAsyncAction(
    UserDataApiActionTypes.GetUserWeightChangePercentageFail,
    {
      asyncKey: UserDataApiActionTypes.GetUserWeightChangePercentage,
      asyncState: 'fail',
    }
  );

  export const getUserTimeSnap = createAsyncAction(
    UserDataApiActionTypes.GetUserTimeSnap,
    {
      asyncKey: UserDataApiActionTypes.GetUserTimeSnap,
      asyncState: 'start',
    },
    props<{ userId: string; monitoredUserId: string; referenceDate?: Date }>()
  );

  export const getUserTimeSnapSuccess = createAsyncAction(
    UserDataApiActionTypes.GetUserTimeSnapSuccess,
    {
      asyncKey: UserDataApiActionTypes.GetUserTimeSnap,
      asyncState: 'success',
    },
    props<{ payload: WeightHistoryData | null }>()
  );

  export const getUserTimeSnapFail = createAsyncAction(UserDataApiActionTypes.GetUserTimeSnapFail, {
    asyncKey: UserDataApiActionTypes.GetUserTimeSnap,
    asyncState: 'fail',
  });

  export const getUserScoreDetails = createAsyncAction(
    UserDataApiActionTypes.GetUserScoreDetails,
    { asyncKey: UserDataApiActionTypes.GetUserScoreDetails, asyncState: 'start' },
    props<{ userId: string; monitoredUserId: string; submissionId?: string }>()
  );

  export const getUserScoreDetailsSuccess = createAsyncAction(
    UserDataApiActionTypes.GetUserScoreDetailsSuccess,
    { asyncKey: UserDataApiActionTypes.GetUserScoreDetails, asyncState: 'success' },
    props<{ payload: UserScoreResponse }>()
  );

  export const getUserScoreDetailsFail = createAsyncAction(
    UserDataApiActionTypes.GetUserScoreDetailsFail,
    { asyncKey: UserDataApiActionTypes.GetUserScoreDetails, asyncState: 'fail' }
  );

  export const checkDataAvailability = createAsyncAction(
    UserDataApiActionTypes.CheckDataAvailability,
    {
      asyncKey: UserDataApiActionTypes.CheckDataAvailability,
      asyncState: 'start',
    },
    props<{ userId: string; monitoredUserId: string }>()
  );

  export const checkDataAvailabilitySuccess = createAsyncAction(
    UserDataApiActionTypes.CheckDataAvailabilitySuccess,
    {
      asyncKey: UserDataApiActionTypes.CheckDataAvailability,
      asyncState: 'success',
    },
    props<{
      payload: {
        hasBasicWebkData: boolean;
        hasSymptomsHistoryDataYesNo: boolean;
        hasSymptomsHistoryDataScale: boolean;
        hasGeneralHealthHistoryData: boolean;
      };
    }>()
  );

  export const checkDataAvailabilityFail = createAsyncAction(
    UserDataApiActionTypes.CheckDataAvailabilityFail,
    {
      asyncKey: UserDataApiActionTypes.CheckDataAvailability,
      asyncState: 'fail',
    }
  );

  export const purgeUserData = createAction(UserDataApiActions.purgeUserData);
}
